"use client";

import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { FC } from "react";

import LeftIcon from "@/svgs/angle_left_b.svg";
import { SIH_URL } from "@/utils";

import { ProfileInfo } from "../profile-info";

import styles from "./col-profile-card.module.scss";
import { ColProfileCardProps } from "./col-profile-card.types";

const ColProfileCard: FC<ColProfileCardProps> = ({ profileRedirect, ...data }) => {
  const { push } = useRouter();

  const [horizontalCarouselRef, horizontalCarouselApi] = useEmblaCarousel({ loop: true });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const handleRedirect = () => {
    if (profileRedirect) {
      push(`/platform/profile/${data.id}`);
    }
  };

  useEffect(() => {
    if (!horizontalCarouselApi) return;

    const updateScrollButtons = () => {
      setCanScrollPrev(horizontalCarouselApi.canScrollPrev());
      setCanScrollNext(horizontalCarouselApi.canScrollNext());
    };

    horizontalCarouselApi.on("select", updateScrollButtons);
    horizontalCarouselApi.on("init", updateScrollButtons);

    updateScrollButtons();

    return () => {
      horizontalCarouselApi.off("select", updateScrollButtons);
      horizontalCarouselApi.off("init", updateScrollButtons);
    };
  }, [horizontalCarouselApi]);

  return (
    <div className={styles.colProfileCard}>
      <div ref={horizontalCarouselRef} className={styles.viewport}>
        <div className={styles.container}>
          {[data.profileImage, ...(data?.photos ?? [])].map((photo, index) => (
            <div key={index} role={"button"} tabIndex={0} className={styles.slide} onClick={handleRedirect}>
              <div className={styles.photoCard}>
                <Image priority fill alt="profile-image" src={SIH_URL({ key: photo?.path })} className={styles.img} />

                <div className={styles.bottomContainer}>
                  <ProfileInfo.ProfileInfoShort {...data} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {data?.photos?.length && (
        <div className={styles.horizontalScrollCarouselButtons}>
          <button disabled={!canScrollPrev} onClick={() => horizontalCarouselApi?.scrollPrev()}>
            <LeftIcon className={styles.icon} />
          </button>
          <button disabled={!canScrollNext} onClick={() => horizontalCarouselApi?.scrollNext()}>
            <LeftIcon className={styles.icon} />
          </button>
        </div>
      )}
    </div>
  );
};

export { ColProfileCard };
